import * as HLF from "html-living-framework";
import { ElementData } from "../Data/ElementData";
import { WorldElement } from "../GameObjects/WorldElement";

export class ParallaxLayer extends PIXI.Container {
    protected _elements: WorldElement[];
    protected _pool: WorldElement[];

    protected _speed: number;
    protected _speedMod: number;
    protected _size: PIXI.Point;
    protected _spawnableElements: ReadonlyArray<ElementData>;
    protected _distToSpawn: number;
    protected _xSpacing: PIXI.Point;
    protected _yRange: PIXI.Point;
    protected _scaleRange: PIXI.Point;
    protected _isSpawning: boolean;

    constructor(pSpeed: number, pSpawnableElements: ReadonlyArray<ElementData>, pXSpacing: PIXI.Point,
                pYRange: PIXI.Point, pScaleRange: PIXI.Point) {
        super();
        this._size = new PIXI.Point();
        this._speed = pSpeed;
        this._speedMod = 1;
        this._spawnableElements = pSpawnableElements;
        this._xSpacing = pXSpacing;
        this._yRange = pYRange;
        this._scaleRange = pScaleRange;
        this._distToSpawn = HLF.Random.floatBetweenPoint(this._xSpacing);
        this._isSpawning = true;

        this._elements = new Array<WorldElement>();
        this._pool = new Array<WorldElement>();
    }

    public set speed(pValue: number) {
        this._speed = pValue;
    }

    public set speedMod(pValue: number) {
        this._speedMod = pValue;
    }

    public get isSpawning(): boolean {
        return this._isSpawning;
    }

    public set isSpawning(pValue: boolean) {
        this._isSpawning = pValue;
    }

    public get elements(): WorldElement[] {
        return this._elements;
    }

    public get yRange(): PIXI.Point {
        return this._yRange;
    }

    protected get leftElement(): WorldElement {
        return this._elements[0];
    }

    protected get rightElement(): WorldElement {
        return this._elements[this._elements.length - 1];
    }

    protected get leftEdge(): number {
        return -this._size.x * 0.5;
    }

    protected get rightEdge(): number {
        return this._size.x * 0.5;
    }

    public init(pSize: PIXI.Point): void {
        this._size.copy(pSize);
        this.update(this._size.x / Math.abs(this._speed));
        for (let i = 0; i < this.elements.length; ++i) {
            this.elements[i].active = false;
        }
    }

    public update(pDeltaTime: number): void {
        // TODO:SH: Math here gets a little fuzzy due to the -speed. Look into clarifying that
        let deltaX: number = pDeltaTime * this._speed * this._speedMod;
        while (this._isSpawning && Math.abs(deltaX) >= this._distToSpawn) {
            this.moveElements(-this._distToSpawn);
            this.spawnNewElement();
            this.sortLayer();
            deltaX += this._distToSpawn;
            this._distToSpawn = this.rightElement.width + HLF.Random.floatBetweenPoint(this._xSpacing);
        }
        this.moveElements(deltaX);
        if (this._isSpawning) {
            this._distToSpawn -= Math.abs(deltaX);
        }
        this.removeOffscreenElements();
        // TODO:SH: Fix ordering of movement + update calls
        for (let i = 0; i < this._elements.length; ++i) {
            this._elements[i].update(pDeltaTime);
        }
    }

    public onResize(pSize: PIXI.Point): void {
        this._size.copy(pSize);
        this.update(0);
    }

    protected moveElements(pDist: number): void {
        for (let i = 0; i < this._elements.length; ++i) {
            this._elements[i].position.x += pDist;
        }
    }

    protected removeOffscreenElements(): void {
        while (this._elements.length > 0 && (this.leftElement.rightEdge < this.leftEdge ||
                this.leftElement.markedForRemoval)) {
            const removed: WorldElement = this._elements.splice(0, 1)[0];
            removed.cleanup();
            this._pool.push(removed);
            this.removeChild(removed);
        }
    }

    protected spawnNewElement(): void {
        const element: WorldElement = this.getElement();
        // Get an elementData and ensure its different than the previous one spawned
        let elementData: ElementData;
        do {
            elementData = HLF.ArrayUtils.random(this._spawnableElements as ElementData[]);
        } while (
            this._spawnableElements.length > 1 &&
            this.rightElement !== undefined &&
            elementData === this.rightElement.data
        );
        element.init(elementData);
        element.position.set(this.rightEdge, this._size.y * 0.5 - HLF.Random.floatBetweenPoint(this._yRange));
        element.scale.set(HLF.Random.floatBetweenPoint(this._scaleRange));
        // TODO:SH: Fix incorrect colliders when flipped
        // element.flipped = element.data.flippable && HLF.Random.bool();
        this.addChild(element);
        element.updateTransform();
        this._elements.push(element);
    }

    protected getElement(): WorldElement {
        if (this._pool.length === 0) {
            this._pool.push(new WorldElement());
        }
        return this._pool.pop()!;
    }

    protected sortLayer(): void {
        const sorted: WorldElement[] = new Array<WorldElement>();
        let temp: WorldElement;
        sorted.push(...this._elements);
        for (let i = 0; i < sorted.length - 1; ++i) {
            for (let j = i + 1; j < sorted.length; ++j) {
                if (sorted[i].position.y > sorted[j].position.y) {
                    temp = sorted[i];
                    sorted[i] = sorted[j];
                    sorted[j] = temp;
                }
            }
        }
        for (let i = 0; i < sorted.length; ++i) {
            sorted[i].parent.setChildIndex(sorted[i], i);
        }
    }
}
